import { useState } from 'react'

import type { Article, MediaPage, ModalItem } from '../../types/domain.d'
import { Icons } from '../../assets'
import * as a9 from '../../analytics'
import * as api from '../../api'
import { NavigationItem } from '../../types/model'
import { isPageAnArticle } from '../../types/utils'
import useFetchReducer from '../useFetchReducer'
import {
  DEFAULT_BAR_ACTIONS,
  GENERATION_MAX_RETRIES,
  GENERATION_RETRY_INTERVAL_MS,
} from '../constants'
import configuration from '../../configuration'

function isActivableNavigationItem(item: NavigationItem): item is ModalItem {
  return [
    NavigationItem,
    NavigationItem.Search,
    NavigationItem.Summary,
    NavigationItem.Coverage,
    NavigationItem.Context,
  ].includes(item)
}

interface ClickMenuParams extends a9.BaseParams {
  name: NavigationItem
}

const getActionsOnPage = (page: MediaPage, isFavorited: boolean) => {
  if (isPageAnArticle(page.urlType)) {
    return DEFAULT_BAR_ACTIONS.map((action) => {
      if (action.name === NavigationItem.Favorite && isFavorited) {
        return { ...action, icon: Icons.StarFilled, disabled: false }
      } else {
        return { ...action, disabled: false }
      }
    })
  } else {
    return DEFAULT_BAR_ACTIONS.map((action) => {
      if (action.name === NavigationItem.Search) {
        return { ...action, disabled: false }
      } else {
        return action
      }
    })
  }
}

export const handleClickHome = () => {
  if (
    import.meta.env.VITE_ENV === 'localhost' &&
    configuration.PAYPER_WEB_BASE_ROUTE
  ) {
    window.location.href = configuration.PAYPER_WEB_BASE_ROUTE
  } else {
    document.location.replace('/explore')
  }
}

const GENERATED_CONTENT_CONFIG = {
  hasGenerationStatus: true,
  retryOnProcessing: {
    intervalMs: GENERATION_RETRY_INTERVAL_MS,
    maxRetries: GENERATION_MAX_RETRIES,
  },
}

const useReadingBar = (
  page: MediaPage,
  initialIsFavorited: boolean,
  setModalItem: React.Dispatch<React.SetStateAction<ModalItem | undefined>>,
) => {
  const [isFavorited, setIsFavorited] = useState(initialIsFavorited)

  const [pageSummary, fetchSummary] = useFetchReducer<string | null>(
    api.getArticleSummary,
    GENERATED_CONTENT_CONFIG,
  )
  const [pageCoverage, fetchCoverage] = useFetchReducer<Article[] | null>(
    api.getArticleCoverage,
  )
  const [pageContext, fetchContext] = useFetchReducer<TrustedHTML | null>(
    api.getArticleContext,
    GENERATED_CONTENT_CONFIG,
  )

  const toggleIsFavorited = async () => {
    try {
      await api.toggleIsFavorited(
        isFavorited,
        page.mediaProviderId,
        page.articleId,
      )
      setIsFavorited((isFavorited) => !isFavorited)
    } catch (e) { }
  }

  const handleFetchSummary = async () => {
    if (!pageSummary.data) {
      await fetchSummary(page.mediaProviderId, page.articleId)
    }
  }

  const handleFetchCoverage = async () => {
    if (!pageCoverage.data) {
      await fetchCoverage(page.mediaProviderId, page.articleId, page.medias)
    }
  }

  const handleFetchContext = async () => {
    if (!pageContext.data) {
      await fetchContext(page.mediaProviderId, page.articleId)
    }
  }

  const handleClickNavItem = (navItem: NavigationItem) => {
    a9.dispatchEvent(a9.Event.CLICK_MENU, {
      name: navItem,
    } as ClickMenuParams)

    setModalItem(isActivableNavigationItem(navItem) ? navItem : undefined)

    const navItemHandlers: Record<NavigationItem, () => void> = {
      [NavigationItem.Home]: handleClickHome,
      [NavigationItem.Favorite]: toggleIsFavorited,
      [NavigationItem.Summary]: handleFetchSummary,
      [NavigationItem.Coverage]: handleFetchCoverage,
      [NavigationItem.Context]: handleFetchContext,
      [NavigationItem.Search]: () => { },
    }

    if (navItem in navItemHandlers) {
      navItemHandlers[navItem]()
    }
  }

  return {
    actions: getActionsOnPage(page, isFavorited),
    handleClickNavItem,
    pageSummary,
    pageCoverage,
    pageContext,
  }
}

export default useReadingBar
